/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { Checkbox } from '@coursera/coursera-ui';

import RichTextView from 'bundles/assess-common/components/RichTextView';
import CMLEditor from 'bundles/authoring/content-authoring/components/CMLEditor';
import { CMLOrHTML, plainTextConverter } from 'bundles/cml';
import type { CmlContent } from 'bundles/cml';
import type {
  OptionsReviewPart,
  OptionsReviewSchema,
  ReviewResponse,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
import AiGradedRubricCoachFeedback from 'bundles/compound-assessments/components/form-parts/AiGradedRubricCoachFeedback';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import ProfileImageCA from 'bundles/compound-assessments/components/shared/ProfileImageCA';
import { GraderTypes, typeNames } from 'bundles/compound-assessments/constants';
import type {
  OptionsPrompt,
  OptionsResponse,
  ResponseMetadata,
  Responses,
} from 'bundles/compound-assessments/types/FormParts';
import { gradingCmlUtils, isFeedbackOnOptionsEnabled } from 'bundles/grading-common';

import _t from 'i18n!nls/assess-common';

export type Props = {
  prompt: OptionsPrompt;
  response?: OptionsResponse;
  responses?: Responses;
  metadata?: ResponseMetadata;
  onChangeResponse: (response: OptionsResponse) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  showValidation: boolean;
  autoFocus?: boolean;
};

const styles = {
  container: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  prompt: css`
    padding: var(--cds-spacing-150);
    border: 1px solid transparent;
  `,
  promptInvalid: css`
    border: 1px solid var(--cds-color-feedback-error);
  `,
  table: css`
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
    margin-bottom: var(--cds-spacing-300);

    td {
      border: 1px solid transparent;
      vertical-align: middle;
      padding: var(--cds-spacing-150);
    }
  `,
  tableInvalid: css`
    td {
      border: 1px solid var(--cds-color-feedback-error);
    }
  `,
  tableRow: css`
    td:nth-child(1) {
      width: 120px;
    }
  `,
  points: css`
    width: 120px;
  `,
  pointsBordered: css`
    box-shadow: inset 4px 0 0 0 var(--cds-color-neutral-stroke-primary);
  `,
  description: css`
    display: flex;
    justify-content: space-between;

    .rc-CML {
      p:last-child {
        margin-bottom: 0;
      }
    }
  `,
  profileImages: css`
    white-space: nowrap;
  `,
  profileImage: () => css`
    display: inline-block;
    width: 28px;

    .rc-ProfileImage {
      .c-profile-image {
        border: 1px solid var(--cds-color-white-0);
        border-collapse: separate;
      }

      .c-profile-initials {
        background-color: #8c8c8c;
      }
    }
  `,
  validationError: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  reviewTextSection: () => css`
    margin: 0 var(--cds-spacing-150) var(--cds-spacing-150) var(--cds-spacing-150);

    h3 {
      > em {
        color: var(--cds-color-neutral-primary-weak);
      }
    }
  `,
  reviewTextLabel: () => css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

export const checkInvalid = (response?: OptionsResponse): FormPartsValidationStatus | null =>
  !((((response || {}).definition || {}).reviewPart || {}).definition || {}).choice
    ? FormPartsValidationStatuses.error
    : null;

const getPeerResponses = (responses: Responses = []) =>
  responses.reduce((result, { response: { definition }, metadata }) => {
    const optionId = (((((definition || {}) as ReviewResponse).reviewPart || {}).definition || {}) as OptionsReviewPart)
      .choice;
    if (optionId) {
      return {
        ...result,
        [optionId]: [...(result[optionId] || []), metadata],
      };
    }
    return result;
  }, {} as Record<string, ResponseMetadata[]>);

const Options = (props: Props) => {
  const { prompt, response, responses, isDisabled, isReadOnly, showValidation, autoFocus } = props;
  const feedbackForOptionsEnabled = isFeedbackOnOptionsEnabled();

  const onOptionSelect = (choice: string) => {
    const { onChangeResponse } = props;

    const newResponse: OptionsResponse = {
      typeName: typeNames.REVIEW_RESPONSE,
      definition: {
        aiSuggestionId: response?.definition.aiSuggestionId,
        graderType: GraderTypes.Human,
        reviewPart: {
          definition: {
            choice,
          },
          typeName: typeNames.OPTIONS,
        },
      },
    };
    if (feedbackForOptionsEnabled) {
      const responseValue = (((response || {}).definition || {}).reviewPart || {}).definition || {};
      newResponse.definition.reviewPart.definition.richTextInput = responseValue.richTextInput;
    }
    onChangeResponse(newResponse);
  };

  const onChangeRichTextInput = (value: CmlContent) => {
    const { onChangeResponse } = props;
    const responseValue = (((response || {}).definition || {}).reviewPart || {}).definition || {};
    const choice = responseValue.choice;
    onChangeResponse({
      typeName: typeNames.REVIEW_RESPONSE,
      definition: {
        aiSuggestionId: response?.definition.aiSuggestionId,
        graderType: GraderTypes.Human,
        reviewPart: {
          definition: {
            choice,
            richTextInput: value,
          },
          typeName: typeNames.OPTIONS,
        },
      },
    });
  };

  if (prompt) {
    const responseValue = (((response || {}).definition || {}).reviewPart || {}).definition || {};
    const chosen = responseValue.choice;
    const richTextInput = responseValue.richTextInput || plainTextConverter.toCML('', gradingCmlUtils.GRADING_DTD_ID);
    const { options = [] } = (((prompt || {}).reviewPartSchemaDetails || {}).definition || {}) as OptionsReviewSchema;
    const peerResponses = getPeerResponses(responses);

    const isInvalid = showValidation && checkInvalid(response);

    // Autofocus on the checked option or the first one if there isn't anything already checked.
    // Iterate over `options` and find the index of the checked option. The `findIndex` returns -1 if there is none.
    const checkedIndex: number =
      (options &&
        options.findIndex(({ id }) => {
          return !!(chosen === id || peerResponses[id]);
        })) ||
      -1; // default to -1 if the `options` are undefined

    // If there isn't any index checked, select the first one to autofocus on.
    const indexToAutoFocus = Math.max(checkedIndex, 0);

    const isAiGraded = prompt.graderType === GraderTypes.Ai;

    return (
      <div css={styles.container}>
        <div css={[styles.prompt, isInvalid && styles.promptInvalid]}>
          <Typography2 variant="bodyPrimary" component="div">
            <CMLOrHTML value={prompt.promptContent} />
          </Typography2>
        </div>
        <table css={[styles.table, isInvalid && styles.tableInvalid]}>
          <tbody>
            {options &&
              options.map(({ id, points, display }: $TSFixMe, index: number) => (
                <tr key={id}>
                  <td css={[styles.points, !!peerResponses[id] && styles.pointsBordered]}>
                    <Checkbox
                      disabled={isDisabled || isAiGraded}
                      onChange={() => onOptionSelect(id)}
                      checked={!!(chosen === id || peerResponses[id])}
                      value={id}
                      inputHtmlAttributes={{
                        ...(autoFocus && index === indexToAutoFocus && { autoFocus: true }),
                      }}
                      isRadio
                      readOnly={isReadOnly}
                      name={prompt.id}
                      {...(isReadOnly
                        ? {
                            uncheckedColor: 'var(--cds-color-grey-200)',
                            uncheckedHoverColor: 'var(--cds-color-grey-200)',
                            checkedColor: 'var(--cds-color-grey-200)',
                            checkedHoverColor: 'var(--cds-color-grey-200)',
                          }
                        : {
                            uncheckedHoverColor: 'var(--cds-color-blue-800)',
                            checkedColor: 'var(--cds-color-blue-700)',
                            checkedHoverColor: 'var(--cds-color-blue-800)',
                          })}
                    >
                      <Typography2 component="h3" variant="subtitleMedium">
                        <FormattedMessage
                          message={_t('{points, plural, =1 {# point} other {# points}}')}
                          points={points || 0}
                        />
                      </Typography2>
                    </Checkbox>
                  </td>
                  <td>
                    <div css={styles.description}>
                      <CMLOrHTML value={display} />
                      {peerResponses[id] && (
                        <div css={styles.profileImages}>
                          {peerResponses[id].map(
                            ({ submitterProfile: { definition: profile }, createdBy }: $TSFixMe) => (
                              <div css={styles.profileImage} key={createdBy}>
                                <ProfileImageCA profile={profile} key={createdBy} />
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {isInvalid && <ValidationError css={styles.validationError} />}

        {feedbackForOptionsEnabled && !isAiGraded && isReadOnly && (
          <RichTextView submissionPart={{ richText: richTextInput }} />
        )}
        {feedbackForOptionsEnabled && !isAiGraded && !isReadOnly && (
          <div css={styles.reviewTextSection}>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.reviewTextLabel}>
              {_t('Comment')}&nbsp;
              <Typography2 variant="subtitleMedium" component="em">
                {_t('(optional)')}
              </Typography2>
            </Typography2>
            <CMLEditor
              key={`text-area-${prompt.id}`}
              initialCML={richTextInput}
              isToolbarCompact
              onContentChange={onChangeRichTextInput}
              customTools={gradingCmlUtils.EDITOR_TOOLS}
              focusOnLoad={false}
              readOnly={isDisabled}
            />
          </div>
        )}
        {feedbackForOptionsEnabled && isAiGraded && (
          <div css={styles.reviewTextSection}>
            <AiGradedRubricCoachFeedback feedback={richTextInput} />
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default Options;
